import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * "Back" is a fact about the JOURNEY, not about the record.
 *
 * Jesse, 2026-09-10: *"if I select a client I want to stay in that context of
 * agreements and invoices that are related to them specifically and not be
 * taken back to a list of invoices… same thing with agreements."* Opening an
 * invoice from a client's Invoices tab and pressing Back landed on `/invoices`
 * — the whole portfolio — so billing one client meant re-finding that client
 * after every hop. An invoice detail page belongs to exactly one client; where
 * the READER came from is the only thing the record itself cannot know.
 *
 * ⚠ **`from` NAMES AN ORIGIN, NEVER A DESTINATION.** It is one word out of a
 * closed set (`RETURN_ORIGINS`) and the page it lands on builds the URL from
 * its OWN data — the invoice's client, the agreement's client. A `?back=<url>`
 * param would be an open redirect wearing a helpful label, and it would also
 * go stale the moment a route moved. Anything unrecognised falls back to the
 * index, which is what an unadorned link already did.
 *
 * It lives in the URL rather than in `location.state` so it survives a
 * refresh, a copied link and the browser's own Back button — the three things
 * router state does not.
 */

export const RETURN_PARAM = 'from';

export const RETURN_ORIGINS = ['client'] as const;
export type ReturnOrigin = (typeof RETURN_ORIGINS)[number];

function isReturnOrigin(value: string | null): value is ReturnOrigin {
  return value !== null && (RETURN_ORIGINS as readonly string[]).includes(value);
}

/** The origin this page was reached from, or null for "arrived on its own". */
export function useReturnOrigin(): ReturnOrigin | null {
  const [params] = useSearchParams();
  const raw = params.get(RETURN_PARAM);
  return isReturnOrigin(raw) ? raw : null;
}

/**
 * Carry an origin onto the next hop, so a chain of screens inside one client
 * (client → agreement → new invoice → the draft) all know their way home.
 * Handles a path that already carries a query and one that carries a hash.
 */
export function withReturn(path: string, origin: ReturnOrigin | null | undefined): string {
  if (!origin) return path;
  const hashAt = path.indexOf('#');
  const hash = hashAt === -1 ? '' : path.slice(hashAt);
  const head = hashAt === -1 ? path : path.slice(0, hashAt);
  const sep = head.includes('?') ? '&' : '?';
  return `${head}${sep}${RETURN_PARAM}=${origin}${hash}`;
}

/**
 * The one "← somewhere" link above a detail page's title. The two that existed
 * differed by a type step and a margin; this is that control, once.
 */
export function BackLink({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center gap-1.5 text-body-sm font-semibold text-grey-500 transition-colors hover:text-secondary',
        className,
      )}
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {children}
    </Link>
  );
}
